/**
 * TenantAdmin.jsx — P4 Multi-tenant admin surface.
 *
 * Surfaces the currently-active tenant's settings, members, FHIR config,
 * and audit log. Every write is gated by `roleMeets()` so viewers /
 * members never see a button they can't click — belt-and-braces with
 * the backend's capability gate which will still 403 anything slipped
 * through.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTenant, roleMeets } from '../contexts/TenantContext';
import * as tenantApi from '../lib/api/tenantApi';
import {
  Building2, Users, ScrollText, Settings, ShieldCheck, UserPlus,
  AlertTriangle, Trash2, Edit3, Check, X, RefreshCw, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

const ROLES = ['viewer', 'member', 'admin', 'owner'];
const AUTH_MODES = ['none', 'bearer', 'smart_backend'];

const RoleBadge = ({ role, testid }) => (
  <span
    data-testid={testid}
    className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded border bg-slate-50 text-slate-700 border-slate-200 capitalize"
  >
    {role}
  </span>
);

function Tabs({ tab, setTab, canReadAudit }) {
  const items = [
    { id: 'members', label: 'Members',     icon: Users },
    { id: 'fhir',    label: 'FHIR Config', icon: ShieldCheck },
    { id: 'settings',label: 'Settings',    icon: Settings },
    ...(canReadAudit ? [{ id: 'audit', label: 'Audit Log', icon: ScrollText }] : []),
  ];
  return (
    <div className="flex items-center gap-1 border-b border-slate-200 mb-4">
      {items.map((it) => (
        <button
          key={it.id}
          data-testid={`tenant-tab-${it.id}`}
          onClick={() => setTab(it.id)}
          className={`flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${
            tab === it.id
              ? 'border-indigo-600 text-indigo-700 font-semibold'
              : 'border-transparent text-slate-600 hover:text-slate-900'
          }`}
        >
          <it.icon className="w-4 h-4" />
          {it.label}
        </button>
      ))}
    </div>
  );
}

// ─── Members tab ──────────────────────────────────────────────────────
function MembersTab({ tenantId, role, isPlatformAdmin }) {
  const canInvite = isPlatformAdmin || roleMeets('admin', role);
  const canModify = canInvite;
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');

  const refresh = useCallback(async () => {
    setLoading(true);
    const r = await tenantApi.listMembers(tenantId);
    if (r.ok) setMembers(r.data?.members || []);
    else       toast.error(r.error?.message || 'Failed to list members');
    setLoading(false);
  }, [tenantId]);
  useEffect(() => { refresh(); }, [refresh]);

  const onInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    const r = await tenantApi.inviteMember(tenantId, { email: inviteEmail.trim(), role: inviteRole });
    setInviting(false);
    if (r.ok) {
      toast.success('Invited');
      setInviteEmail('');
      refresh();
    } else {
      toast.error(r.error?.message || 'Invite failed');
    }
  };

  const onChangeRole = async (m, newRole) => {
    const r = await tenantApi.updateMember(tenantId, m.user_id, { role: newRole });
    if (r.ok) { toast.success('Role updated'); refresh(); }
    else       toast.error(r.error?.message || 'Update failed');
  };

  const onRemove = async (m) => {
    if (!window.confirm(`Remove ${m.email || m.user_id}?`)) return;
    const r = await tenantApi.removeMember(tenantId, m.user_id);
    if (r.ok) { toast.success('Removed'); refresh(); }
    else       toast.error(r.error?.message || 'Remove failed');
  };

  return (
    <div data-testid="tenant-members-tab">
      {canInvite && (
        <form
          data-testid="tenant-invite-form"
          onSubmit={onInvite}
          className="flex flex-wrap items-center gap-2 mb-4 p-3 rounded bg-slate-50 border border-slate-200"
        >
          <UserPlus className="w-4 h-4 text-indigo-600" />
          <input
            data-testid="tenant-invite-email"
            type="email"
            required
            placeholder="email@example.com"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            className="flex-1 min-w-[200px] px-2 py-1.5 border border-slate-300 rounded text-sm bg-white"
          />
          <select
            data-testid="tenant-invite-role"
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value)}
            className="px-2 py-1.5 border border-slate-300 rounded text-sm bg-white capitalize"
          >
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <button
            data-testid="tenant-invite-submit"
            type="submit"
            disabled={inviting}
            className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded disabled:opacity-50 inline-flex items-center gap-1"
          >
            {inviting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
            Invite
          </button>
        </form>
      )}

      {loading ? (
        <div data-testid="tenant-members-loading" className="p-8 text-center text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> Loading members…
        </div>
      ) : (
        <table data-testid="tenant-members-table" className="min-w-full text-sm border border-slate-200 rounded overflow-hidden">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
            <tr>
              <th className="px-3 py-2 text-left">Email</th>
              <th className="px-3 py-2 text-left">User id</th>
              <th className="px-3 py-2 text-left">Role</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Invited by</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr
                key={m.user_id}
                className="border-t border-slate-100"
                data-testid={`tenant-member-row-${m.user_id}`}
              >
                <td className="px-3 py-2">{m.email || <em className="text-slate-400">—</em>}</td>
                <td className="px-3 py-2 font-mono text-[11px] text-slate-500 max-w-[200px] truncate">{m.user_id}</td>
                <td className="px-3 py-2">
                  {canModify ? (
                    <select
                      data-testid={`tenant-member-role-${m.user_id}`}
                      value={m.role}
                      onChange={(e) => onChangeRole(m, e.target.value)}
                      className="px-2 py-0.5 border border-slate-300 rounded text-xs capitalize bg-white"
                    >
                      {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  ) : <RoleBadge role={m.role} />}
                </td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded border ${m.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                    {m.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs text-slate-500 font-mono">{m.invited_by || '—'}</td>
                <td className="px-3 py-2 text-right">
                  {canModify && (
                    <button
                      data-testid={`tenant-member-remove-${m.user_id}`}
                      onClick={() => onRemove(m)}
                      className="p-1 rounded hover:bg-rose-100 text-rose-600"
                      title="Remove member"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {members.length === 0 && (
              <tr><td colSpan="6" className="p-6 text-center text-slate-500" data-testid="tenant-members-empty">No members yet.</td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── FHIR config tab ──────────────────────────────────────────────────
function FhirConfigTab({ tenantId, role, isPlatformAdmin }) {
  const canEdit = isPlatformAdmin || roleMeets('admin', role);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    const r = await tenantApi.getTenant(tenantId);
    if (r.ok) setCfg(r.data?.fhir_config || {});
    else       toast.error(r.error?.message || 'Failed to load FHIR config');
  }, [tenantId]);
  useEffect(() => { refresh(); }, [refresh]);

  const onSave = async () => {
    setSaving(true);
    const r = await tenantApi.updateTenant(tenantId, { fhir_config: cfg });
    setSaving(false);
    if (r.ok) { toast.success('Saved'); setCfg(r.data?.fhir_config || cfg); }
    else       toast.error(r.error?.message || 'Save failed');
  };

  if (!cfg) return <div data-testid="tenant-fhir-loading" className="p-8 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline-block" /></div>;

  const set = (k, v) => setCfg({ ...cfg, [k]: v });

  return (
    <div data-testid="tenant-fhir-tab" className="space-y-3 max-w-2xl">
      {!canEdit && (
        <div data-testid="tenant-fhir-readonly" className="p-2 rounded bg-slate-50 border border-slate-200 text-xs text-slate-600">
          Read-only — your role does not include FHIR config writes.
        </div>
      )}
      <Field label="Base URL" testid="tenant-fhir-base-url"
             value={cfg.base_url || ''} onChange={(v) => set('base_url', v)} disabled={!canEdit}
             placeholder="https://fhir.example.org" />
      <Field label="Token URL" testid="tenant-fhir-token-url"
             value={cfg.token_url || ''} onChange={(v) => set('token_url', v)} disabled={!canEdit}
             placeholder="https://auth.example.org/oauth2/token" />
      <div className="flex flex-col gap-1">
        <label className="text-xs font-semibold text-slate-700">Auth mode</label>
        <select
          data-testid="tenant-fhir-auth-mode"
          value={cfg.auth_mode || 'none'}
          onChange={(e) => set('auth_mode', e.target.value)}
          disabled={!canEdit}
          className="px-2 py-1.5 border border-slate-300 rounded text-sm bg-white capitalize disabled:bg-slate-100"
        >
          {AUTH_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>
      <Field label="Client ID reference" testid="tenant-fhir-client-id"
             value={cfg.client_id_ref || ''} onChange={(v) => set('client_id_ref', v)} disabled={!canEdit}
             placeholder="secret ref — e.g. vault://fhir/client-id" />
      <Field label="Client secret reference" testid="tenant-fhir-client-secret"
             value={cfg.client_secret_ref || ''} onChange={(v) => set('client_secret_ref', v)} disabled={!canEdit}
             placeholder="secret ref — never the value" />
      <label className="inline-flex items-center gap-2 text-sm text-slate-700">
        <input
          data-testid="tenant-fhir-write-enabled"
          type="checkbox"
          checked={!!cfg.write_enabled}
          onChange={(e) => set('write_enabled', e.target.checked)}
          disabled={!canEdit}
        />
        FHIR write enabled
      </label>
      {canEdit && (
        <button
          data-testid="tenant-fhir-save"
          onClick={onSave}
          disabled={saving}
          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded inline-flex items-center gap-2 disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          Save changes
        </button>
      )}
    </div>
  );
}

function Field({ label, value, onChange, disabled, testid, placeholder }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-semibold text-slate-700">{label}</label>
      <input
        data-testid={testid}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        className="px-2 py-1.5 border border-slate-300 rounded text-sm disabled:bg-slate-100"
      />
    </div>
  );
}

// ─── Settings tab ─────────────────────────────────────────────────────
function SettingsTab({ tenantId, role, isPlatformAdmin, onDeleted }) {
  const canEdit = isPlatformAdmin || roleMeets('admin', role);
  const canDelete = isPlatformAdmin || roleMeets('owner', role);
  const [tenant, setTenant] = useState(null);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { (async () => {
    const r = await tenantApi.getTenant(tenantId);
    if (r.ok) {
      setTenant(r.data);
      setName(r.data?.name || '');
    }
  })(); }, [tenantId]);

  const onSave = async () => {
    setSaving(true);
    const r = await tenantApi.updateTenant(tenantId, { name });
    setSaving(false);
    if (r.ok) { toast.success('Saved'); setTenant(r.data); }
    else       toast.error(r.error?.message || 'Save failed');
  };

  const onDelete = async () => {
    if (!window.confirm(`Permanently delete tenant "${tenant?.name}"? This cannot be undone.`)) return;
    const r = await tenantApi.deleteTenant(tenantId);
    if (r.ok) { toast.success('Tenant deleted'); onDeleted?.(); }
    else       toast.error(r.error?.message || 'Delete failed');
  };

  if (!tenant) return <div data-testid="tenant-settings-loading" className="p-8 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline-block" /></div>;

  return (
    <div data-testid="tenant-settings-tab" className="space-y-4 max-w-xl">
      <Field label="Tenant name" testid="tenant-settings-name"
             value={name} onChange={setName} disabled={!canEdit} />
      <div className="text-xs text-slate-500">
        Slug: <code data-testid="tenant-settings-slug" className="bg-slate-100 px-1 rounded">{tenant.slug}</code>
        <span className="mx-2">·</span>
        Status: <code className="bg-slate-100 px-1 rounded">{tenant.status}</code>
      </div>
      {canEdit && (
        <button
          data-testid="tenant-settings-save"
          onClick={onSave}
          disabled={saving}
          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded inline-flex items-center gap-2 disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          Save
        </button>
      )}

      {canDelete && (
        <div className="mt-8 p-4 rounded border border-rose-200 bg-rose-50" data-testid="tenant-danger-zone">
          <div className="flex items-center gap-2 text-rose-800 font-semibold text-sm mb-2">
            <AlertTriangle className="w-4 h-4" /> Danger zone
          </div>
          <p className="text-xs text-rose-700 mb-2">Deleting this tenant removes all memberships and cascades the audit log. Subscription registry rows are not automatically deleted.</p>
          <button
            data-testid="tenant-settings-delete"
            onClick={onDelete}
            className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white text-sm rounded inline-flex items-center gap-1"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete tenant
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Audit tab ────────────────────────────────────────────────────────
function AuditTab({ tenantId }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    setLoading(true);
    const r = await tenantApi.listAudit(tenantId, 200);
    if (r.ok) setEntries(r.data?.entries || []);
    else       toast.error(r.error?.message || 'Failed to load audit');
    setLoading(false);
  }, [tenantId]);
  useEffect(() => { refresh(); }, [refresh]);
  return (
    <div data-testid="tenant-audit-tab">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-slate-600">Latest {entries.length} entries</p>
        <button
          data-testid="tenant-audit-refresh"
          onClick={refresh}
          disabled={loading}
          className="text-xs text-indigo-600 inline-flex items-center gap-1"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>
      <div className="border border-slate-200 rounded overflow-hidden">
        {entries.map((e) => (
          <div key={e.id} className="border-b border-slate-100 last:border-b-0 px-3 py-2 text-xs bg-white" data-testid={`tenant-audit-entry-${e.id}`}>
            <div className="flex items-center gap-2">
              <code className="bg-slate-100 px-1 rounded text-[10px]">{e.action}</code>
              <span className="text-slate-500">{e.target_type} · {e.target_id}</span>
              <span className="ml-auto text-slate-400">{e.at}</span>
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5 font-mono">actor: {e.actor_user_id}</div>
          </div>
        ))}
        {entries.length === 0 && !loading && (
          <div data-testid="tenant-audit-empty" className="p-6 text-center text-slate-500 text-sm">No audit entries yet.</div>
        )}
      </div>
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────
export default function TenantAdmin() {
  const { activeTenant, activeId, role, isPlatformAdmin, refresh: refreshCtx } = useTenant();
  const [tab, setTab] = useState('members');

  if (!activeId || !activeTenant) {
    return (
      <div data-testid="tenant-admin-empty" className="p-8 max-w-xl mx-auto text-center text-slate-500">
        <Building2 className="w-10 h-10 mx-auto mb-2 text-slate-400" />
        Select a tenant from the switcher to manage it.
      </div>
    );
  }

  const canReadAudit = isPlatformAdmin || roleMeets('admin', role);

  return (
    <div data-testid="tenant-admin" className="p-6 max-w-5xl mx-auto">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Building2 className="w-6 h-6 text-indigo-600" />
            {activeTenant.tenant_name}
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            <code className="bg-slate-100 px-1 rounded">{activeTenant.tenant_slug}</code>
            <span className="mx-2">·</span>
            Your role: <RoleBadge role={role} testid="tenant-admin-your-role" />
          </p>
        </div>
      </div>

      <Tabs tab={tab} setTab={setTab} canReadAudit={canReadAudit} />

      {tab === 'members'  && <MembersTab    tenantId={activeId} role={role} isPlatformAdmin={isPlatformAdmin} />}
      {tab === 'fhir'     && <FhirConfigTab tenantId={activeId} role={role} isPlatformAdmin={isPlatformAdmin} />}
      {tab === 'settings' && <SettingsTab   tenantId={activeId} role={role} isPlatformAdmin={isPlatformAdmin}
                                            onDeleted={() => refreshCtx()} />}
      {tab === 'audit' && canReadAudit && <AuditTab tenantId={activeId} />}
    </div>
  );
}
